import bcrypt from 'bcryptjs';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { useLibraryComponentContext } from '~/library/components/LibraryComponentContext';

import { REACT_APP_OLD_PASSWORD_HASH } from './constants';
import { putChangePassword } from './data';

export type PasswordData = {
  fLoginPassword: string;
  fLoginNewPassword: string;
};

export type IFormInput = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type UseChangePasswordOutput = {
  formMethods: UseFormReturn<IFormInput>;
  onSave: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  open: boolean;
};

export const useLegacyChangePassword = (): UseChangePasswordOutput => {
  const { onUpdateLegacy } = useLibraryComponentContext();

  const { t } = useTranslation();

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>({
      currentPassword: yup.string().required(t('fieldIsRequired')),
      newPassword: yup
        .string()
        .required(t('fieldIsRequired'))
        .min(6, t('laValidPassword')),
      confirmPassword: yup
        .string()
        .required(t('fieldIsRequired'))
        .oneOf([yup.ref('newPassword')], t('laNewPassNotMatch')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const formMethods = useForm<IFormInput>({
    resolver,
  });
  const { handleSubmit } = formMethods;

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = React.useState(false);
  const userProfile = useLoggedInEmployeeProfile();
  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      // Hash old password
      const strOldPasswordHash: string = bcrypt.hashSync(
        data.currentPassword,
        REACT_APP_OLD_PASSWORD_HASH,
      );

      // Hash new password
      const strNewPasswordHash: string = bcrypt.hashSync(
        data.newPassword,
        REACT_APP_OLD_PASSWORD_HASH,
      );

      const json: PasswordData = {
        fLoginPassword: strOldPasswordHash,
        fLoginNewPassword: strNewPasswordHash,
      };
      (async () => {
        try {
          const response = await putChangePassword(
            json,
            userProfile.username as string,
          );
          if (response) {
            enqueueSnackbar(t('successPasswordChanged'), {
              variant: 'success',
            });
            window.location.replace('/page-logout');

            if (onUpdateLegacy) {
              onUpdateLegacy(['changePasswordUpdate']);
            }
          } else {
            enqueueSnackbar(t('laPasswordResetFail'), {
              variant: 'error',
            });
          }
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [enqueueSnackbar, handleSubmit, t, userProfile.username, onUpdateLegacy]);

  useEffect(() => {
    setOpen(true);
  }, []);

  const onSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return {
    formMethods,
    onSave,
    handleClose,
    open,
  };
};
